// Fichier: src/components/UpdateReport.js
import React, { useState } from 'react';

const UpdateReport = ({ client, update, onClose }) => {
  const [isSending, setIsSending] = useState(false);
  const [reportFormat, setReportFormat] = useState('markdown');
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getStatusEmoji = (status) => {
    switch(status) {
      case 'completed': return '✅';
      case 'partial': return '⚠️';
      case 'cancelled': return '❌';
      default: return '🔄';
    }
  };
  
  const generateReport = () => {
    let report = '';
    
    if (reportFormat === 'markdown') {
      // Format Markdown pour Slack
      report = `# 📋 Compte-rendu de mise à jour\n\n`;
      report += `**Client:** ${client.name}\n`;
      report += `**Date:** ${formatDate(update.date)}\n`;
      report += `**Titre:** ${update.title}\n`;
      report += `**Type:** ${update.updateType}\n`;
      report += `**Statut:** ${getStatusEmoji(update.status)} ${getStatusLabel(update.status)}\n\n`;
      
      if (update.description) {
        report += `## Description\n${update.description}\n\n`;
      }
      
      if (update.ticketUrl) {
        report += `**Ticket:** ${update.ticketUrl}\n\n`;
      }
      
      if ((update.status === 'cancelled' || update.status === 'partial') && update.statusReason) {
        report += `## ${update.status === 'cancelled' ? "Raison de l'annulation" : "Détails de la réalisation partielle"}\n`;
        report += `${update.statusReason}\n\n`;
      }
      
      // Section MISE À JOUR
      report += `## 🔧 Actions effectuées\n`;
      if (update.update.dbSchemaUpdated) report += `- ✅ MAJ du schéma de base de données\n`;
      if (update.update.servicesRestarted) report += `- ✅ Redémarrage des services\n`;
      if (update.update.mobileAppUpdated) report += `- ✅ MAJ Application mobile\n`;
      if (update.update.buildsUpdated) report += `- ✅ Mise à jour des builds\n`;
      if (update.update.mobileAppCreated) report += `- ✅ Création application mobile\n`;
      if (update.update.internalTestsCompleted) report += `- ✅ Tests internes réalisés\n`;
      if (update.update.appDeployedToStores) report += `- ✅ Mise en téléchargement sur les stores\n`;
      
      if (update.update.notes) {
        report += `\n_Notes: ${update.update.notes}_\n`;
      }
      report += '\n';
      
      // Section GENERAL
      report += `## 🌐 Vérifications générales\n`;
      report += `### Production\n`;
      if (update.general.production.secureClientConnection) report += `- ✅ Connexion sécurisée\n`;
      if (update.general.production.softwareComponentsRecovery) report += `- ✅ Récupération briques logicielles\n`;
      if (update.general.production.softwareDependenciesRecovery) report += `- ✅ Récupération dépendances\n`;
      
      report += `\n### Vérification fonctionnelle\n`;
      if (update.general.verification.carePortalDisplay) report += `- ✅ Affichage portail soignant\n`;
      if (update.general.verification.carePortalLogin) report += `- ✅ Connexion portail soignant\n`;
      if (update.general.verification.patientSearch) report += `- ✅ Recherche patient\n`;
      if (update.general.verification.pairingSheetGeneration) report += `- ✅ Feuille d'appairage\n`;
      if (update.general.verification.patientPortalDisplay) report += `- ✅ Affichage portail patient\n`;
      if (update.general.verification.patientPortalLogin) report += `- ✅ Connexion portail patient\n`;
      
      if (update.general.verification.notes) {
        report += `\n_Notes: ${update.general.verification.notes}_\n`;
      }
      report += '\n';
      
      // Section COMMITS
      if (hasCommits(update.commits)) {
        report += `## 💻 Versions déployées\n`;
        if (update.commits.manager) report += `- **Manager:** ${update.commits.manager}\n`;
        if (update.commits.marshallerFrontal) report += `- **Marshaller Frontal:** ${update.commits.marshallerFrontal}\n`;
        if (update.commits.marshallerInterop) report += `- **Marshaller Interop:** ${update.commits.marshallerInterop}\n`;
        if (update.commits.patientApp) report += `- **Patient App:** ${update.commits.patientApp}\n`;
        if (update.commits.datamodel) report += `- **Datamodel:** ${update.commits.datamodel}\n`;
        
        if (update.commits.notes) {
          report += `\n_Notes: ${update.commits.notes}_\n`;
        }
        report += '\n';
      }
      
      if (update.additionalNotes) {
        report += `## 📝 Notes additionnelles\n${update.additionalNotes}\n`;
      }
      
    } else {
      // Format texte simple
      report = `COMPTE-RENDU DE MISE À JOUR\n`;
      report += `${'='.repeat(50)}\n\n`;
      report += `Client: ${client.name}\n`;
      report += `Date: ${formatDate(update.date)}\n`;
      report += `Titre: ${update.title}\n`;
      report += `Type: ${update.updateType}\n`;
      report += `Statut: ${getStatusEmoji(update.status)} ${getStatusLabel(update.status)}\n\n`;
      
      if (update.description) {
        report += `Description:\n${update.description}\n\n`;
      }
      
      // ... (version texte simple similaire)
    }
    
    return report;
  };
  
  const hasCommits = (commits) => {
    return commits && (commits.manager || commits.marshallerFrontal || 
           commits.marshallerInterop || commits.patientApp || commits.datamodel);
  };
  
  const getStatusLabel = (status) => {
    switch(status) {
      case 'completed': return 'Complétée';
      case 'partial': return 'Partielle';
      case 'cancelled': return 'Annulée';
      default: return 'En cours';
    }
  };
  
  const copyToClipboard = () => {
    const report = generateReport();
    navigator.clipboard.writeText(report).then(() => {
      alert('Compte-rendu copié dans le presse-papiers !');
    });
  };
  
  const downloadReport = () => {
    const report = generateReport();
    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compte-rendu-${client.name}-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  const sendToSlack = async () => {
    if (!client.slackChannelId) {
      alert('Aucun canal Slack configuré pour ce client. Veuillez ajouter un ID de canal Slack dans les paramètres du client.');
      return;
    }
    
    setIsSending(true);
    const report = generateReport();
    
    try {
      const response = await fetch('/api/slack/send-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          channelId: client.slackChannelId,
          message: report
        })
      });
      
      if (response.ok) {
        alert('Compte-rendu envoyé sur Slack avec succès !');
        onClose();
      } else {
        const error = await response.json();
        alert(`Erreur lors de l'envoi sur Slack : ${error.message || 'Erreur inconnue'}`);
      }
    } catch (error) {
      alert(`Erreur lors de l'envoi sur Slack : ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Export du compte-rendu de mise à jour
          </h3>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Format du rapport :
            </label>
            <select 
              value={reportFormat} 
              onChange={(e) => setReportFormat(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="markdown">Markdown (recommandé pour Slack)</option>
              <option value="text">Texte simple</option>
            </select>
          </div>
          
          <div className="mb-4 p-4 bg-gray-50 rounded border border-gray-200">
            <h4 className="font-medium mb-2">Aperçu :</h4>
            <pre className="whitespace-pre-wrap text-sm max-h-96 overflow-y-auto">
              {generateReport()}
            </pre>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            >
              Fermer
            </button>
            
            <button
              onClick={copyToClipboard}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Copier
            </button>
            
            <button
              onClick={downloadReport}
              className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            >
              Télécharger
            </button>
            
            <button
              onClick={sendToSlack}
              disabled={isSending || !client.slackChannelId}
              className={`px-4 py-2 rounded ${
                isSending || !client.slackChannelId
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-purple-500 hover:bg-purple-600'
              } text-white`}
            >
              {isSending ? 'Envoi...' : 'Envoyer sur Slack'}
            </button>
          </div>
          
          {!client.slackChannelId && (
            <p className="text-sm text-amber-600 mt-2">
              ⚠️ Aucun canal Slack configuré. Ajoutez un ID de canal dans les paramètres du client.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdateReport;